import { z } from 'zod';

/**
 * Flake intelligence contracts (Migration 070 / Delivery roadmap
 * Tier 3b.1).
 *
 * The aggregator worker reads `test_runs.step_results` daily and
 * produces a `(flow, step, day)` row of (runs, failed, retried,
 * healed, flake_rate). The dashboard renders top offenders across
 * a configurable window; the weekly digest fans the same payload
 * out via the notification-router.
 */

export const FLAKE_TIME_WINDOWS = ['7d', '30d', '90d'] as const;
export type FlakeTimeWindow = (typeof FLAKE_TIME_WINDOWS)[number];
export const FlakeTimeWindowSchema = z.enum(FLAKE_TIME_WINDOWS);

export const FlakeStatRowSchema = z.object({
  flowId: z.string().uuid(),
  flowName: z.string(),
  stepId: z.string(),
  stepTitle: z.string(),
  windowStart: z.string(),
  runs: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  retried: z.number().int().nonnegative(),
  healed: z.number().int().nonnegative(),
  flakeRate: z.number().min(0).max(1),
});
export type FlakeStatRow = z.infer<typeof FlakeStatRowSchema>;

export const FlakeStatTopRowSchema = z.object({
  flowId: z.string().uuid(),
  flowName: z.string(),
  stepId: z.string(),
  stepTitle: z.string(),
  /** Sum of runs across the window. */
  runs: z.number().int().nonnegative(),
  /** Sum of failed runs across the window. */
  failed: z.number().int().nonnegative(),
  /** Sum of healed runs across the window. */
  healed: z.number().int().nonnegative(),
  /** failed / runs across the window. */
  flakeRate: z.number().min(0).max(1),
  /** Trend points for the sparkline; oldest first. */
  trend: z.array(
    z.object({
      day: z.string(),
      flakeRate: z.number().min(0).max(1),
      runs: z.number().int().nonnegative(),
    }),
  ),
});
export type FlakeStatTopRow = z.infer<typeof FlakeStatTopRowSchema>;

export const ListFlakeTopResponseSchema = z.object({
  window: FlakeTimeWindowSchema,
  generatedAt: z.string(),
  rows: z.array(FlakeStatTopRowSchema),
});
export type ListFlakeTopResponse = z.infer<typeof ListFlakeTopResponseSchema>;

/**
 * Window length in days. Strict mapping so the aggregator and the
 * dashboard agree.
 */
export function windowToDays(window: FlakeTimeWindow): number {
  switch (window) {
    case '7d':
      return 7;
    case '30d':
      return 30;
    case '90d':
      return 90;
  }
}

/**
 * The minimum number of runs a step must have over the window
 * before we surface it. Below this the flake-rate signal is
 * dominated by sample size.
 */
export const FLAKE_MIN_RUNS_FOR_VISIBILITY = 5;

/**
 * Compute a per-step flake rate from a stream of run statuses.
 * Pure function — used by the worker and by the unit tests.
 *
 * Returns:
 *   - `null` if `runs` is below `FLAKE_MIN_RUNS_FOR_VISIBILITY`.
 *   - 0 when every run passed (no flake).
 *   - 1 when every run failed (consistent failure, not flake).
 *
 * Both extremes are valid outputs but the dashboard hides 0-rate
 * rows because the user only cares about the noisy ones.
 */
export function flakeRateFor(stats: { runs: number; failed: number }): number | null {
  if (stats.runs < FLAKE_MIN_RUNS_FOR_VISIBILITY) return null;
  if (stats.runs === 0) return null;
  return Math.max(0, Math.min(1, stats.failed / stats.runs));
}

/**
 * Sort comparator for the dashboard. Higher flake_rate first;
 * ties broken by total runs (more data = stronger signal).
 */
export function compareFlakeRows(a: FlakeStatTopRow, b: FlakeStatTopRow): number {
  if (b.flakeRate !== a.flakeRate) return b.flakeRate - a.flakeRate;
  return b.runs - a.runs;
}
